import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';

const MyRCAStat = () => {
  const location = useLocation();
  const authState = useSelector(state => state.authReducer);
  const [userId, setUserId] = useState(authState.user._id);
  const [rcas, setRcas] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tags, setTags] = useState('');
  const [rcaType, setRcaType] = useState('');
  const [rcaTypeHead, setRcaTypeHead] = useState('');
  const rcaTypeInitialized = useRef(false);
  const [selectedRcaId, setSelectedRcaId] = useState(null);

  const fetchRcasForStat = useCallback((rcaType) => {
    const config = {
        url: "/rca/myrcastat",
        method: "post",
        headers: { Authorization: authState.token },
        data: { userId: userId, rcaType: rcaType }  // Ensure rcaType is included in the data
    };
    // console.log("Getting "+rcaType+" RCA",config);

    fetchData(config, { showSuccessToast: false })
        .then(response => {
            // console.log("Response:", response);
            if (response && Array.isArray(response.usersrca)) {
                setRcas(response.usersrca);
            } else {
                console.error("Invalid response format: usersrca not found in response");
            }
        })
        .catch(error => {
            console.error("Error fetching rcas:", error);
        });
  }, [authState.token, fetchData, userId]);

  useEffect(() => {
      if (!authState.isLoggedIn) return;
      fetchRcasForStat(rcaType);  // Ensure rcaType is passed
    }, [authState.isLoggedIn, fetchRcasForStat, rcaType]);

  const handleView = (rcaId) => {
    setSelectedRcaId(prevId => (prevId === rcaId ? null : rcaId));
  };

  const handleRcaShare = (rcaId) => {
    setSelectedRcaId(rcaId);
  };

  const handleRcaSave = (rcaId) => {
    setSelectedRcaId(rcaId);
  };

  const handleRcaPrint = (rcaId) => {
    setSelectedRcaId(rcaId);
  };

  const formatDate = (date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateTime = (date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const sortedRcas = [...rcas].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  const groupAndSummarizeRcas = (rcas) => {
    const groupedData = rcas.reduce((acc, rca) => {
      const dateKey = formatDate(new Date(rca.dateCreated));
      const rcaTypeKey = rca.rcaType;
  
      if (!acc[dateKey]) {
        acc[dateKey] = {};
      }
      if (!acc[dateKey][rcaTypeKey]) {
        acc[dateKey][rcaTypeKey] = {
          isActiveCount: 0,
          isSharedCount: 0,
          teamCode: new Set(),
          pairCode: new Set(),
          taskCode: new Set(),
        };
      }
  
      acc[dateKey][rcaTypeKey].isActiveCount += rca.isActive ? 1 : 0;
      acc[dateKey][rcaTypeKey].isSharedCount += rca.isShared ? 1 : 0;
      acc[dateKey][rcaTypeKey].teamCode.add(rca.teamCode);
      acc[dateKey][rcaTypeKey].pairCode.add(rca.pairCode);
      acc[dateKey][rcaTypeKey].taskCode.add(rca.taskCode);
  
      return acc;
    }, {});
  
    // Convert the grouped data into an array format
    return Object.entries(groupedData).map(([date, types]) => ({
      date,
      types: Object.entries(types).map(([type, counts]) => ({
        rcaType: type,
        isActiveCount: counts.isActiveCount,
        isSharedCount: counts.isSharedCount,
        teamCodeCount: counts.teamCode.size,
        pairCodeCount: counts.pairCode.size,
        taskCodeCount: counts.taskCode.size,
      }))
    }));
  };
  
  const summarizedData = groupAndSummarizeRcas(rcas);

  return (
    <div className='bg-black [&_p]:text-white'>
      <div className='flex flex-row justify-start p-4  border-b border-blue-400'>
        <h2 className='text-xl lg:text-2xl mt-1 mx-2 text-yellow-200'>5Whys Analysis - My RCA Stat</h2>
      </div>
      <div className="min-h-[80vh] my-2 mx-auto py-4">
        <div className="p-4 mt-4">
          {loading ? (
            <Loader />
          ) : (
            <>
              {summarizedData.length === 0 ? (
                <div className='h-[300px] flex items-center justify-center gap-4 text-yellow-300'>
                  <h3 className='text-xl mt-1 mx-2 text-yellow-200'>No RCA Record Found!</h3>
                </div>
              ) : (
                <div className="flex-1 justify-center items-center text-center w-full text-m lg:text-lg">
                  <table className="w-full text-white bg-black">
                    <thead>
                      <tr className='text-white bg-black'>
                        <th className="py-2 px-2 border-b w-5/10">Date</th>
                        <th className="py-2 px-2 border-b w-1/10">RCA Type</th>
                        <th className="py-2 px-2 border-b w-1/10">Active</th>
                        <th className="py-2 px-2 border-b w-1/10">Team</th>
                        <th className="py-2 px-2 border-b w-1/10">Pair</th>
                        <th className="py-2 px-2 border-b w-1/10">Task Linked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summarizedData.map((dateGroup) =>
                        dateGroup.types.map((typeGroup, index) => (
                          <tr className='text-white bg-black' key={`${dateGroup.date}-${index}`}>
                            <td className="py-2 px-2 border-b bg-black text-white">{dateGroup.date}</td>
                            <td className="py-2 px-2 border-b text-white">{typeGroup.rcaType}</td>
                            <td className="py-2 px-2 border-b bg-black text-white">{typeGroup.isSharedCount}</td>
                            <td className="py-2 px-2 border-b text-white">{typeGroup.teamCodeCount}</td>
                            <td className="py-2 px-2 border-b bg-black text-white">{typeGroup.pairCodeCount}</td>
                            <td className="py-2 px-2 border-b text-white">{typeGroup.taskCodeCount}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <footer className="bg-gray-900 border-t border-blue-400 text-gray-400 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>        
    </div>
  );
};

export default MyRCAStat;
